// Inside-out cylinder textured with the panoramic JPEG uploaded by the
// mobile scanner. Renders behind the semantic walls so cabinets and wall
// snap-guides remain in front; the panorama supplies the "feel" of the
// room (light, wallpaper, window views) the walls themselves don't.
//
// Capture model: see cabinet_mobile/docs/textured_room.md.
//
// Caveats — known and accepted at MVP:
//   - Cylinder radius is fixed (not derived from actual room walls), so
//     the panorama may visibly clip past or fall short of the wall in
//     extreme room sizes. Default 300 inches (~7.5 m) covers most
//     kitchen/wardrobe scans.
//   - Yaw alignment uses panorama column 0 = world yaw 0. The capture
//     code on mobile sets bucket = floor(yawDeg), so this mapping is
//     correct as long as the mobile and web sides agree on
//     "yaw 0 = looking down world +Z" (default Unity / Three.js for our
//     scenes). If alignment is visibly off, rotate this cylinder by
//     `-panorama.yawRangeRad[0]` around Y.

import { useTexture } from "@react-three/drei";
import * as THREE from "three";

import type { RoomPanorama as RoomPanoramaT } from "@/lib/types";

const RADIUS_INCHES = 300;
const RADIAL_SEGMENTS = 64;

interface Props {
  panorama: RoomPanoramaT;
  /** Default ceiling height from the Room — sets the cylinder height. */
  ceilingHeight: number;
}

export function RoomPanorama({ panorama, ceilingHeight }: Props) {
  const url = `/db/default/fs${panorama.ref}`;
  const texture = useTexture(url);
  // The JPEG is sRGB-encoded; mark it so Three's lighting doesn't double-gamma.
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;

  const [cx, , cz] = panorama.capturePoint;

  return (
    <mesh position={[cx, panorama.eyeHeight, cz]}>
      <cylinderGeometry args={[
        RADIUS_INCHES,
        RADIUS_INCHES,
        ceilingHeight,
        RADIAL_SEGMENTS,
        1,
        /*openEnded*/ true,
      ]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} toneMapped={false} />
    </mesh>
  );
}
